import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, ChefHat, Tag, X, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cart = () => {
  const { items, removeItem, updateQuantity, totalPrice, totalItems } = useCart();
  const { isAuthenticated } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  // Translations
  const texts = {
    tr: {
      pageTitle: 'Sepetim',
      itemsCount: 'ürün',
      emptyTitle: 'Sepetiniz Boş',
      emptyDesc: 'Lezzetli yemeklerimizi keşfetmek için menümüze göz atın.',
      browseMenu: 'Menüyü İncele',
      orderSummary: 'Sipariş Özeti',
      discountCoupon: 'İndirim Kuponu',
      couponPlaceholder: 'Kupon kodu',
      apply: 'Uygula',
      discount: 'indirim',
      subtotal: 'Ara Toplam',
      couponDiscount: 'Kupon İndirimi',
      delivery: 'Teslimat',
      free: 'Ücretsiz',
      total: 'Toplam',
      checkout: 'Siparişi Tamamla',
      loginContinue: 'Giriş Yap ve Devam Et',
      paymentNote: 'Kapıda nakit veya kredi kartı ile ödeme',
      enterCoupon: 'Lütfen kupon kodu girin',
      invalidCoupon: 'Geçersiz kupon kodu'
    },
    en: {
      pageTitle: 'My Cart',
      itemsCount: 'items',
      emptyTitle: 'Your Cart is Empty',
      emptyDesc: 'Browse our menu to discover delicious dishes.',
      browseMenu: 'Browse Menu',
      orderSummary: 'Order Summary',
      discountCoupon: 'Discount Coupon',
      couponPlaceholder: 'Coupon code',
      apply: 'Apply',
      discount: 'discount',
      subtotal: 'Subtotal',
      couponDiscount: 'Coupon Discount',
      delivery: 'Delivery',
      free: 'Free',
      total: 'Total',
      checkout: 'Checkout',
      loginContinue: 'Login and Continue',
      paymentNote: 'Pay with cash or credit card on delivery',
      enterCoupon: 'Please enter a coupon code',
      invalidCoupon: 'Invalid coupon code'
    },
    me: {
      pageTitle: 'Moja Korpa',
      itemsCount: 'artikala',
      emptyTitle: 'Vaša Korpa je Prazna',
      emptyDesc: 'Pregledajte naš meni da otkrijete ukusna jela.',
      browseMenu: 'Pregledaj Meni',
      orderSummary: 'Pregled Narudžbe',
      discountCoupon: 'Kupon za Popust',
      couponPlaceholder: 'Kupon kod',
      apply: 'Primijeni',
      discount: 'popust',
      subtotal: 'Međuzbir',
      couponDiscount: 'Popust Kupona',
      delivery: 'Dostava',
      free: 'Besplatno',
      total: 'Ukupno',
      checkout: 'Završi Narudžbu',
      loginContinue: 'Prijavi se i Nastavi',
      paymentNote: 'Plaćanje gotovinom ili karticom pri dostavi',
      enterCoupon: 'Molimo unesite kupon kod',
      invalidCoupon: 'Nevažeći kupon kod'
    }
  };

  const txt = texts[language] || texts.en;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error(txt.enterCoupon);
      return;
    }
    
    setCouponLoading(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode,
        subtotal: totalPrice
      });
      
      setAppliedCoupon(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || txt.invalidCoupon);
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleCheckout = () => {
    const tableNum = sessionStorage.getItem('tableNumber');
    if (tableNum) {
      // Guest table order - go directly to checkout without login
      navigate(`/checkout?table=${tableNum}&guest=true`, { state: { couponCode: appliedCoupon?.code } });
    } else if (!isAuthenticated) {
      navigate('/login', { state: { from: '/checkout' } });
    } else {
      // Pass coupon info to checkout
      navigate('/checkout', { state: { couponCode: appliedCoupon?.code } });
    }
  };

  const discountAmount = appliedCoupon ? appliedCoupon.discount_amount : 0;
  const finalTotal = totalPrice - discountAmount;

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center px-4"
        >
          <ShoppingBag className="w-20 h-20 text-charcoal/20 mx-auto mb-6" />
          <h2 className="font-playfair text-2xl font-semibold text-charcoal mb-4">
            {txt.emptyTitle}
          </h2>
          <p className="text-charcoal/60 mb-8">
            {txt.emptyDesc}
          </p>
          <Link to="/alakart">
            <Button 
              className="bg-terracotta text-white hover:bg-terracotta/90 rounded-full px-8"
              data-testid="browse-menu-btn"
            >
              {txt.browseMenu}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pb-32">
      {/* Header */}
      <div className="bg-sand py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-2">
              {txt.pageTitle}
            </h1>
            <p className="text-charcoal/60">
              {totalItems} {txt.itemsCount}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            <AnimatePresence mode="popLayout">
              {items.map((item) => (
                <motion.div
                  key={`${item.item_id}-${item.item_type}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  layout
                  className="bg-white rounded-2xl p-6 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {item.item_type === 'catering' && (
                          <ChefHat className="w-4 h-4 text-basil" />
                        )}
                        <span className="text-xs font-medium text-charcoal/50 uppercase">
                          {item.item_type === 'catering' ? 'Catering' : 'Alakart'}
                        </span>
                      </div>
                      <h3 className="font-playfair text-lg font-semibold text-charcoal mb-1">
                        {item.name}
                      </h3>
                      {item.catering_detail && (
                        <p className="text-sm text-charcoal/60 mt-2">
                          {item.catering_detail}
                        </p>
                      )}
                      <p className="text-terracotta font-bold mt-3">
                        €{item.price.toFixed(2)}
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-3">
                      <button
                        onClick={() => removeItem(item.item_id, item.item_type)}
                        className="p-2 text-charcoal/40 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        data-testid={`remove-item-${item.item_id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                      
                      <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1">
                        <button
                          onClick={() => updateQuantity(item.item_id, item.item_type, item.quantity - 1)}
                          className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 transition-colors"
                          data-testid={`cart-quantity-minus-${item.item_id}`}
                        >
                          <Minus className="w-4 h-4 text-charcoal" />
                        </button>
                        <span className="w-8 text-center font-medium text-charcoal">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.item_id, item.item_type, item.quantity + 1)}
                          className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-stone-50 transition-colors"
                          data-testid={`cart-quantity-plus-${item.item_id}`}
                        >
                          <Plus className="w-4 h-4 text-charcoal" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
              <h2 className="font-playfair text-xl font-semibold text-charcoal mb-6">
                {txt.orderSummary}
              </h2>
              
              {/* Coupon Code Section */}
              <div className="mb-6 p-4 bg-stone-50 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-5 h-5 text-terracotta" />
                  <span className="font-medium text-charcoal">{txt.discountCoupon}</span>
                </div>
                
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-semibold text-green-700">{appliedCoupon.code}</p>
                        <p className="text-sm text-green-600">%{appliedCoupon.discount_percent} {txt.discount}</p>
                      </div>
                    </div>
                    <button 
                      onClick={handleRemoveCoupon}
                      className="p-1 hover:bg-green-100 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5 text-green-600" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder={txt.couponPlaceholder}
                      className="flex-1"
                      data-testid="coupon-input"
                    />
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={couponLoading}
                      variant="outline"
                      className="px-4"
                      data-testid="apply-coupon-btn"
                    >
                      {couponLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        txt.apply
                      )}
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-charcoal/70">
                  <span>{txt.subtotal}</span>
                  <span>€{totalPrice.toFixed(2)}</span>
                </div>
                
                {appliedCoupon && (
                  <div className="flex justify-between text-green-600">
                    <span>{txt.couponDiscount} ({appliedCoupon.discount_percent}%)</span>
                    <span>-€{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-charcoal/70">
                  <span>{txt.delivery}</span>
                  <span className="text-basil">{txt.free}</span>
                </div>
                
                <div className="border-t border-stone-200 pt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold text-charcoal">{txt.total}</span>
                    <span className="font-playfair text-xl font-bold text-terracotta">
                      €{finalTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                className="w-full bg-terracotta text-white hover:bg-terracotta/90 rounded-full py-6 text-base font-semibold"
                data-testid="checkout-btn"
              >
                {sessionStorage.getItem('tableNumber') ? txt.checkout : (isAuthenticated ? txt.checkout : txt.loginContinue)}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <p className="text-center text-charcoal/50 text-sm mt-4">
                {txt.paymentNote}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
